import { call, CallEffect, delay, ForkEffect, put, race, select, take, TakeEffect, takeEvery } from 'redux-saga/effects';

import * as authActions from '../actions/auth';
import * as settingsActions from '../actions/settings';
import * as uiActions from '../actions/ui';

import { AppState } from 'models/app-store';
import { IGetSettingsResponse } from 'models/http/setting';
import { IGetActiveThemeResponse } from 'models/http/themes';
import { ModuleCodes } from 'models/modules';
import { IModule } from 'models/permission';
import { ISession } from 'models/session';
import { SettingsEntityTypes } from 'models/settings';
import moment from 'moment';
import { LocalStorage, SessionStorage, TableLayoutLocalStoragePrefix } from 'utils/enums/storage';
import AuthHttp from 'utils/hooks/http/auth.http';
import SettingHttp from 'utils/hooks/http/setting.http';
import ThemesHttp from 'utils/hooks/http/themes.http';
import localState from 'utils/local-storage';
import { IAutoLoginResponse, IRefreshTokenResponse } from '../../models/http/auth';
import { LocaleDirectionMap } from 'models/i18n';
import { LanguageDirection, LanguagesCodes } from 'utils/enums/language';
import sessionState from 'utils/session-storage';
import { OperationTypes } from 'utils/enums/system-template';

// ======================= Workers (Effects) =======================

// Login
function* loginSaga() {
  let loggedIn: boolean = yield true;

  while (loggedIn) {
    const accessExpirationCurrent: number | null = yield select((state: AppState) => state.auth.session?.accessExpiration || null);

    const accessDelayTime = yield moment()
      .add({ seconds: (accessExpirationCurrent || 0) - 30 })
      .diff(moment()); // refresh 30 seconds before expiration time

    const theme = yield localState.load<string>(LocalStorage.Theme);

    yield put(uiActions.setTheme(theme));

    const refreshExpiration = yield localState.load<number>(LocalStorage.RefreshExpiration);
    const refreshDelayTime = yield moment().add({ seconds: refreshExpiration }).diff(moment());

    const [accessDelayAction, refreshDelayAction, logoutAction]: [CallEffect, CallEffect, TakeEffect] = yield race<CallEffect | TakeEffect>(
      [
        delay(accessDelayTime < 0 ? 0 : accessDelayTime),
        delay(refreshDelayTime < 0 ? 0 : refreshDelayTime),
        take(authActions.AuthActions.Logout),
      ],
    );

    if (accessDelayAction) {
      try {
        const { accessToken, accessExpiration } = yield call(async () => {
          const http = new AuthHttp();

          return Promise.all([
            localState.load<string>(LocalStorage.RefreshToken),
            sessionState.load<string>(SessionStorage.SiteId),
            sessionState.load<string>(SessionStorage.CompanyId),
          ]).then(async ([refreshToken, siteId, companyId]: [string | null, string | null, string | null]) => {
            if (!refreshToken) {
              throw new Error('No refresh token - should be in this phase');
            }

            if (!siteId) {
              throw new Error('No siteId - should be in this phase');
            }

            if (!companyId) {
              throw new Error('No companyId - should be in this phase');
            }

            return http
              .refreshToken(refreshToken, { siteId: Number(siteId), companyId: Number(companyId) })
              .then((response: IRefreshTokenResponse) => response);
          });
        });

        yield put(authActions.setAccessToken(accessToken, accessExpiration));
      } catch {
        loggedIn = yield false;
      }
    }

    if (refreshDelayAction) {
      loggedIn = yield false;
    }

    if (!loggedIn) {
      yield put(authActions.logout());
    }

    if (logoutAction) {
      loggedIn = yield false;
    }
  }
}

// Logout
function* logoutSaga() {
  yield localStorage.removeItem(LocalStorage.RefreshToken);
  yield localStorage.removeItem(LocalStorage.RefreshExpiration);
}

// Auto Login
function* autoLoginSaga() {
  yield put(uiActions.showSpinner(true));

  const language = yield call(() => localState.load<string>(LocalStorage.Language));
  const fallbackLanguage = import.meta.env.VITE_DEFAULT_LANAGUAGE || 'en';
  const normalizedLanguage = ((language || fallbackLanguage).split(':')[0] || fallbackLanguage) as LanguagesCodes;

  if (normalizedLanguage !== language) {
    yield call(() => localState.save(LocalStorage.Language, normalizedLanguage));
  }

  yield put(uiActions.setLanguage(normalizedLanguage));

  try {
    const response: IAutoLoginResponse<boolean> = yield call(async () => {
      const http = new AuthHttp();

      const [refreshToken, deviceToken, currentSiteId, currentCompanyId, lastViewedSiteId, lastViewedCompanyId] = await Promise.all([
        localState.load<string>(LocalStorage.RefreshToken),
        localState.load<string>(LocalStorage.DeviceToken),
        sessionState.load<string>(SessionStorage.SiteId),
        sessionState.load<string>(SessionStorage.CompanyId),
        localState.load<string>(LocalStorage.LastSiteViewed),
        localState.load<string>(LocalStorage.LastCompanyViewed),
      ]);

      /**
       * We are taking the view of the current tab (in case, for example, the user just refresgh it)
       * If it doesn't exist, and the user just opened another tab, but has a sessioned opened, we will take it from the last view from local storaeg
       **/
      const siteId = currentSiteId || lastViewedSiteId;
      const companyId = currentCompanyId || lastViewedCompanyId;

      if (!refreshToken) {
        return;
      }

      const params: Parameters<typeof http.autoLogin> = [refreshToken];

      if (companyId && siteId) {
        params.push(siteId, companyId);
      }

      if (deviceToken) {
        params.push(deviceToken);
      }

      // Continue with auto login only if site and company IDs are available
      return http.autoLogin(...params).then((response: IAutoLoginResponse<boolean>) => response);
    });

    if (response.access.isCompanyAdmin) {
      yield put(authActions.setPermissions([{ action: '', moduleCode: ModuleCodes.All }]));
    } else {
      const modules: IModule[] = [];

      response.access.permissions.forEach((permission: IAutoLoginResponse<false>['access']['permissions'][number]) => {
        const [view, action] = permission.split('.');

        modules.push({ action: action, moduleCode: view });
      });

      yield put(authActions.setPermissions(modules));
    }

    const session: ISession = {
      isCompanyAdmin: response.access.isCompanyAdmin,
      view: response.view,
      companies: response.companies,
      accessToken: response.tokens.accessToken,
      accessExpiration: response.tokens.accessExpiration,
      user: { ...response.user, roleName: response.access.roleName },
      theme: response.theme,
    };

    sessionState.save(SessionStorage.SiteId, response.view.site.toString());
    sessionState.save(SessionStorage.CompanyId, response.view.company.toString());

    yield put(authActions.setSession(session));

    const settings: IGetSettingsResponse = yield call(async () => {
      const http = new SettingHttp();

      return http
        .get({ entityType: SettingsEntityTypes.Site, accessToken: response.tokens.accessToken })
        .then((response: IGetSettingsResponse) => response);
    });

    const themeData: IGetActiveThemeResponse = yield call(async () => {
      const http = new ThemesHttp();

      return http
        .getActive(response.tokens.accessToken)
        .then((response: IGetActiveThemeResponse) => response)
        .catch(console.error);
    });

    yield put(settingsActions.setSettings(settings.settings));
    const localeDirection = LocaleDirectionMap[normalizedLanguage];

    if (themeData.theme) {
      yield put(
        uiActions.setThemes(
          { dark: themeData.theme.dark, light: themeData.theme.light },
          themeData.theme.fontFamily || localeDirection == LanguageDirection.RTL ? 'Open Sans' : null,
        ),
      );
    } else {
      yield put(uiActions.setThemes({ dark: null, light: null }, localeDirection == LanguageDirection.RTL ? 'Open Sans' : null));
    }

    if (settings.currentRouteTemplateId) {
      yield put(settingsActions.setRouteTemplate(settings.currentRouteTemplateId));
    }

    if (settings.currentAssignTemplateId) {
      yield put(settingsActions.setAssignTemplate(settings.currentAssignTemplateId));
    }
  } catch {
    yield call(clearLocalStorage);
    /** On any auto-login failure, making sure we terminate the session with explicit logout */
    yield put(authActions.logout());
  }

  return yield put(uiActions.showSpinner(false));
}

// Clear Local Storage
function* clearLocalStorage() {
  /** Backup device token and language settings */
  const deviceToken = yield call(() => localState.load<string>(LocalStorage.DeviceToken));
  const deviceLanguage = yield call(() => localState.load<string>(LocalStorage.Language));
  const tableLayouts: [string, string][] = yield call(() => {
    const cachedLayouts: [string, string][] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);

      if (!key || !key.startsWith(TableLayoutLocalStoragePrefix)) {
        continue;
      }

      const value = localStorage.getItem(key);

      if (value === null) {
        continue;
      }

      cachedLayouts.push([key, value]);
    }

    return cachedLayouts;
  });

  /** Clear local storage */
  localStorage.clear();

  /** Re-save backed-up settings */
  deviceToken && localState.save(LocalStorage.DeviceToken, deviceToken);
  deviceLanguage && localState.save(LocalStorage.Language, deviceLanguage);
  tableLayouts.forEach(([key, value]: [string, string]) => localStorage.setItem(key, value));
}

// Switch View
function* switchViewSaga() {
  const sideEffectHandler = yield select((state: AppState) => state.auth.switchViewSideEffectsHandler);

  const view: {
    company: number;
    site: number;
    operation: OperationTypes;
  } = yield select((state: AppState) => state.auth.session?.view);

  if (!view) {
    throw new Error('View does not exist but must exist before changing view');
  }

  const siteId = view.site.toString();
  const companyId = view.company.toString();

  /** Session storage save - for reloading the page to keep the same site and company view of the current tab */
  sessionState.save(SessionStorage.SiteId, siteId);
  sessionState.save(SessionStorage.CompanyId, companyId);

  /** For new tab that doesnt have session storage to open the same view of the last chosen one */
  localState.save(LocalStorage.LastSiteViewed, siteId);
  localState.save(LocalStorage.LastCompanyViewed, companyId);

  yield sideEffectHandler?.();
}

// ======================= Watchers =======================

export function* watchAuth(): IterableIterator<ForkEffect> {
  yield takeEvery(authActions.AuthActions.Logout, logoutSaga);
  yield takeEvery(authActions.AuthActions.AutoLogin, autoLoginSaga);
  yield takeEvery(authActions.AuthActions.SetSession, loginSaga);
  yield takeEvery(authActions.AuthActions.SetView, switchViewSaga);
}
